import type {Auth, Department, PortaroVersion} from 'src/typings';
import {downloadBlobAsFile, generateFilename, serializeObject} from 'core/debug-util/utils';
import type {InMemoryLogger} from 'core/logging/in-memory-logger';

declare global {
    interface Window {
        serverUrl: string;
        currentAuth: Auth;
        currentDep: Department;
        portaroVersion: PortaroVersion;
    }
}

export function generateAndDownloadReport(logger: InMemoryLogger, optionalInfo: string = '') {
    const reportContent = generateTextReport(logger.dumpLogHistory(), optionalInfo);
    const blob = new Blob([reportContent], {type: 'text/plain'});
    const filename = generateFilename('debug-report', 'txt');
    downloadBlobAsFile(blob, filename);
}

function generateTextReport(logHistory: string[] = [], optionalInfo: string = ''): string {
    let report = '======================================\n';
    report += ` Bug Report Generated: ${new Date().toUTCString()}\n`;
    report += '======================================\n\n';

    // --- Device and Browser Info ---
    report += '--- User Agent & Device Info ---\n';
    report += `Location: ${window.location.href}\n`;
    report += `User Agent: ${navigator.userAgent}\n`;
    report += `Platform: ${navigator.platform}\n`;
    report += `Language: ${navigator.language}\n`;
    report += `Screen Resolution: ${window.screen.width}x${window.screen.height}\n`;
    report += `Viewport Size: ${window.innerWidth}x${window.innerHeight}\n`;
    report += `Cookie Enabled: ${navigator.cookieEnabled}\n\n`;

    // --- Portaro Config Info ---
    report += '--- Catalog Config ---\n';
    report += `currentAuth: ${serializeObject(window.currentAuth)}\n`;
    report += `currentDep: ${serializeObject(window.currentDep)}\n`;
    report += `portaroVersion: ${window.portaroVersion.humanReadableString}\n`;
    report += `serverUrl: ${window.serverUrl}\n\n`;

    // --- Performance Metrics ---
    report += '--- Browser Performance Metrics ---\n';
    // Navigation Timing API
    const navigationTiming = performance.getEntriesByType(
        'navigation'
    )[0] as PerformanceNavigationTiming;
    if (navigationTiming) {
        report += `Page Load Time: ${(navigationTiming.duration / 1000).toFixed(2)}s\n`;
        report += `Time to First Byte (TTFB): ${(navigationTiming.responseStart / 1000).toFixed(2)}s\n`;
        report += `DOM Interactive: ${(navigationTiming.domInteractive / 1000).toFixed(2)}s\n`;
        report += `DOM Complete: ${(navigationTiming.domComplete / 1000).toFixed(2)}s\n`;
    }
    // Memory API (Note: Not available in all browsers and may be imprecise)
    const memory = (performance as any).memory;
    if (memory) {
        report += `JS Heap Size Limit: ${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB\n`;
        report += `Total JS Heap Size: ${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB\n`;
        report += `Used JS Heap Size: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB\n`;
    }
    report += '\n';

    // --- Optional Info (e.g., Stack Trace) ---
    if (optionalInfo) {
        report += '--- Additional Context (e.g., Error Stack Trace) ---\n';
        report += `${optionalInfo}\n\n`;
    }

    // --- Console Log Dump ---
    report += '--- Log Dump ---\n';
    if (logHistory.length === 0) {
        report += 'NO LOGS CAPTURED\n';
    } else {
        report += logHistory.join('\n');
    }
    report += '\n\n=== End of Report ===';

    return report;
}